import { motion } from 'framer-motion';
import { useMemo, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { getSiteRoleLabel } from '../data/siteUsers';
import { useSiteAuth } from '../hooks/useSiteAuth';

export default function SignIn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session, signIn, signUp, isInitializing, runtime, runtimeError } = useSiteAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupPasswordConfirm, setSignupPasswordConfirm] = useState('');
  const [signupRole, setSignupRole] = useState<'vendor' | 'resource_vendor' | 'adult_model' | 'parent' | 'donor'>(
    searchParams.get('role') === 'resource_vendor'
      ? 'resource_vendor'
      : searchParams.get('role') === 'adult_model'
        ? 'adult_model'
        : searchParams.get('role') === 'parent'
          ? 'parent'
          : searchParams.get('role') === 'donor'
            ? 'donor'
            : 'vendor',
  );
  const [error, setError] = useState<string | null>(null);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);

  const redirectTo = searchParams.get('redirect') || '/portal';
  const guidedSignupPrompt = useMemo(
    () =>
      searchParams.get('role') === 'vendor' ||
      searchParams.get('role') === 'resource_vendor' ||
      searchParams.get('role') === 'adult_model' ||
      searchParams.get('role') === 'parent' ||
      searchParams.get('role') === 'donor',
    [searchParams],
  );

  if (session) {
    return <Navigate to={redirectTo} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await signIn(email, password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to sign in right now.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleVendorSignUp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSignupError(null);
    setIsSigningUp(true);

    try {
      if (signupPassword.length < 8) {
        throw new Error('Passwords must be at least 8 characters.');
      }

      if (signupPassword !== signupPasswordConfirm) {
        throw new Error('The password and confirmation do not match.');
      }

      await signUp({
        name: signupName,
        email: signupEmail,
        password: signupPassword,
        role: signupRole,
      });

      navigate(redirectTo, { replace: true });
    } catch (err) {
      setSignupError(err instanceof Error ? err.message : 'Unable to create a vendor account right now.');
    } finally {
      setIsSigningUp(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <motion.section
          className="auth-copy"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
        >
          <div className="space-y-6">
            <span className="eyebrow auth-eyebrow">Site portal</span>
            <p className="auth-kicker">Protected access for staff, families, supporters, and event partners.</p>
            <h1>
              {guidedSignupPrompt
                ? 'Protected actions now run through a signed-in portal account.'
                : 'Sign in through the live Victoria&apos;s Place Bliss portal.'}
            </h1>
            <p className="auth-lead">
              {guidedSignupPrompt
                ? 'Vendor, resource-vendor, adult model, parent, and donor flows now rely on a signed-in site-user account before the protected action unlocks.'
                : 'This portal now uses the real Bliss site-user auth endpoints instead of preview accounts. Sign in with an active Victoria&apos;s Place site-user account to reach the protected workspace.'}
            </p>
          </div>

          <div className="auth-feature-list">
            <div>
              <span>Project-owned roles</span>
              <p>Bliss reads the role set from the Victoria&apos;s Place project runtime instead of using the fallback defaults.</p>
            </div>
            <div>
              <span>Real site-user sessions</span>
              <p>Portal access is now verified against the live Bliss app auth flow and current site-user record.</p>
            </div>
            <div>
              <span>Participant-ready foundation</span>
              <p>Vendor, resource-vendor, adult model, parent, and donor accounts can now sign up here and return to the right protected flow.</p>
            </div>
          </div>

          {runtime ? (
            <div className="auth-runtime-block">
              <div className="auth-runtime-heading">
                <p className="info-kicker">Runtime status</p>
                <p>
                  Connected to project <strong>{runtime.projectId}</strong>
                  {runtime.roleSource ? <> via <strong>{runtime.roleSource}</strong></> : null}.
                </p>
              </div>
              <div className="auth-role-list">
                {runtime.allowedRoles.map((role) => (
                  <span key={role} className="auth-role-chip">
                    {getSiteRoleLabel(role)}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div className="auth-footer-note">
            <Link to="/" className="button-secondary tap-target">
              Return to public site
            </Link>
            <p>
              Vendors, resource vendors, adult models, parents, and donors can create an account here. Other portal roles are still managed by Victoria&apos;s Place.
            </p>
          </div>
        </motion.section>

        <motion.section
          className="auth-panel"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.08, ease: 'easeOut' }}
        >
          <div className="auth-form-shell">
            <div className="auth-form-heading">
              <p className="info-kicker">Portal sign in</p>
              <h2>Enter your site-user credentials.</h2>
              <p>Use the same email and password assigned to your Victoria&apos;s Place portal account.</p>
            </div>

            <form className="auth-form-grid" onSubmit={handleSubmit}>
              <label className="auth-field">
                <span>Email</span>
                <input
                  className="auth-input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@victoriasplace.org"
                  required
                />
              </label>

              <label className="auth-field">
                <span>Password</span>
                <input
                  className="auth-input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Enter your password"
                  required
                />
              </label>

              {runtimeError ? <div className="auth-error">{runtimeError}</div> : null}
              {error ? <div className="auth-error">{error}</div> : null}

              <button
                type="submit"
                className="button-primary tap-target auth-submit"
                disabled={isSubmitting || isInitializing || !!runtimeError}
              >
                {isInitializing ? 'Connecting to Bliss...' : isSubmitting ? 'Signing in...' : 'Sign in'}
              </button>
            </form>

            <div className="auth-runtime-note">
              <p className="info-kicker">Need help?</p>
              <p>
                Admins can add or update site users from the Bliss project dashboard. This portal now expects a real
                Bliss hosted runtime, so sign-in works from a live site domain or Bliss site preview where
                <code>/api/site/bootstrap</code> is available.
              </p>
            </div>

            <div className="auth-divider" />

            <div className="auth-form-heading">
              <p className="info-kicker">Portal sign up</p>
              <h2>Create the account required for event participation access.</h2>
              <p>Choose the right role, create the account, and you&apos;ll be sent back to finish the matching profile and event flow.</p>
            </div>

            <form className="auth-form-grid" onSubmit={handleVendorSignUp}>
              <label className="auth-field">
                <span>Account type</span>
                <select
                  className="auth-input"
                  value={signupRole}
                  onChange={(event) =>
                    setSignupRole(event.target.value as 'vendor' | 'resource_vendor' | 'adult_model' | 'parent' | 'donor')
                  }
                >
                  <option value="vendor">Vendor</option>
                  <option value="resource_vendor">Resource vendor</option>
                  <option value="adult_model">Adult model</option>
                  <option value="parent">Parent or guardian</option>
                  <option value="donor">Donor</option>
                </select>
              </label>

              <label className="auth-field">
                <span>Name</span>
                <input
                  className="auth-input"
                  type="text"
                  autoComplete="name"
                  value={signupName}
                  onChange={(event) => setSignupName(event.target.value)}
                  placeholder="Your name"
                  required
                />
              </label>

              <label className="auth-field">
                <span>Email</span>
                <input
                  className="auth-input"
                  type="email"
                  autoComplete="email"
                  value={signupEmail}
                  onChange={(event) => setSignupEmail(event.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </label>

              <label className="auth-field">
                <span>Password</span>
                <input
                  className="auth-input"
                  type="password"
                  autoComplete="new-password"
                  value={signupPassword}
                  onChange={(event) => setSignupPassword(event.target.value)}
                  placeholder="Create a password"
                  required
                />
              </label>

              <label className="auth-field">
                <span>Confirm password</span>
                <input
                  className="auth-input"
                  type="password"
                  autoComplete="new-password"
                  value={signupPasswordConfirm}
                  onChange={(event) => setSignupPasswordConfirm(event.target.value)}
                  placeholder="Confirm your password"
                  required
                />
              </label>

              {runtimeError ? <div className="auth-error">{runtimeError}</div> : null}
              {signupError ? <div className="auth-error">{signupError}</div> : null}

              <button
                type="submit"
                className="button-secondary tap-target auth-submit"
                disabled={isSigningUp || isInitializing || !!runtimeError}
              >
                {isSigningUp
                  ? 'Creating account...'
                  : signupRole === 'donor'
                    ? 'Create donor account'
                  : signupRole === 'adult_model'
                    ? 'Create adult model account'
                    : signupRole === 'parent'
                      ? 'Create parent account'
                    : 'Create portal account'}
              </button>
            </form>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
